import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { App } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SharedTransportForm from '../components/SharedTransportForm';
import TransportRouteFormPageHeader from '../components/TransportRouteFormPageHeader';
import { normalizeOrderOptions, type OrderOptionDto } from '../dto/order';
import type { SupplierOrderDto } from '../dto/supplierOrder';
import { fetchSupplierOrders } from '../modules/orders/api/supplierOrdersApi';
import { canManageTransportRoutes } from '../modules/transports/transportRouteAccessPolicy';
import { getTransportErrorMessage, isBlockingTransportError } from '../modules/transports/transportErrorMessages';
import { useFeedback } from '../utils/feedback';
import api from '../services/api';
import { authService } from '../services/auth';
import { useBackNavigation } from '../hooks/useBackNavigation';
import './TransportRouteFormPage.css';
import { submitWithOperationalException } from '../modules/operationalExceptions/operationalExceptionFlow';
import { cargoOverflowCorrectionOption } from '../modules/transports/cargoOverflowCorrection';
import { createTransportRoute, fetchTransportFormDefaults } from '../modules/transports/api/transportRoutesApi';
import { normalizeTransportKind } from '../modules/transports/transportDirection';
import { readTransportReturnContext, resolveTransportReturnPath, withTransportReturnContext } from '../modules/transports/transportReturnContext';
import { getTransportLabel } from '../utils/transportLabel';
import { showTransportSaveWarnings } from '../modules/transports/transportSaveWarnings';
import { incompleteValuationWarning, readIncompleteValuationIssues } from '../modules/orders/stockValuationSaveWarning';

const CreateTransportRoute: React.FC = () => {
  const { t } = useTranslation(['transport', 'common']);
  const feedback = useFeedback();
  const { modal } = App.useApp();
  const navigate = useNavigate();
  const goBack = useBackNavigation();
  const [searchParams] = useSearchParams();
  const presetOrderId = searchParams.get('orderId') || undefined;
  // Decision 2026-10-04: presets from the ZD / ZK card.
  const presetSupplierOrderId = searchParams.get('supplierOrderId') || undefined;
  const presetKind = normalizeTransportKind(searchParams.get('kind'));
  const initialSupplierOrderIds = useMemo(
    () => (presetSupplierOrderId ? [presetSupplierOrderId] : undefined),
    [presetSupplierOrderId],
  );
  const [warehouseAddress, setWarehouseAddress] = useState<string | null>(null);
  const returnContext = readTransportReturnContext(searchParams);
  const initialOrderIds = useMemo(
    () => (presetOrderId ? [presetOrderId] : undefined),
    [presetOrderId],
  );
  const [availableOrders, setAvailableOrders] = useState<OrderOptionDto[]>([]);
  const scopedOrder = presetOrderId
    ? availableOrders.find((order) => order.id === presetOrderId)
    : undefined;
  // Etap 2 (transport przywozowy): candidates for the inbound (ZD) link.
  const [availableSupplierOrders, setAvailableSupplierOrders] = useState<SupplierOrderDto[]>([]);
  const [supplierOrdersLoading, setSupplierOrdersLoading] = useState(true);
  const [supplierOrdersLoadError, setSupplierOrdersLoadError] = useState(false);
  const [loading, setLoading] = useState(false);
  const currentUser = authService.getCurrentUser();
  const canManageTransports = canManageTransportRoutes(currentUser);
  const canEditTransportCosts = authService.canEditTransportCosts();
  const canViewTransportCosts = authService.canViewTransportCosts();
  const canViewGoodsValue = authService.canViewCustomerSalePrices();
  // Owner 2026-10-14: a truck starts on a ZD card (from the factory) or a ZK
  // card (from our warehouse); without that start this page creates nothing.
  const startedFromOrder = Boolean(presetOrderId || presetSupplierOrderId);

  const fetchOrders = useCallback(async () => {
    try {
      const response = await api.get('/orders');
      setAvailableOrders(normalizeOrderOptions(response.data, 'transport.create.availableOrders'));
    } catch (error) {
      feedback.error(t('transport:errorFetchingOrders'));
    }
  }, [feedback, t]);

  // Etap 2: a failed supplier-order fetch keeps the outbound flow usable while
  // the inbound picker exposes an explicit error and retry action.
  const fetchSupplierOrdersForLinking = useCallback(async () => {
    setSupplierOrdersLoading(true);
    setSupplierOrdersLoadError(false);
    try {
      setAvailableSupplierOrders(await fetchSupplierOrders());
    } catch (error) {
      setAvailableSupplierOrders([]);
      setSupplierOrdersLoadError(true);
    } finally {
      setSupplierOrdersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canManageTransports) {
      navigate('/app/transports', { replace: true });
      return;
    }
    if (!startedFromOrder) {
      feedback.info(t('transport:createFromOrderHint'), { key: 'transport-create-from-order' });
      navigate('/app/transports', { replace: true });
      return;
    }

    void fetchOrders();
    void fetchSupplierOrdersForLinking();
    // The warehouse address only prefills; without it the field stays empty.
    void fetchTransportFormDefaults()
      .then((defaults) => setWarehouseAddress(defaults.warehouseAddress))
      .catch(() => setWarehouseAddress(null));
  }, [canManageTransports, startedFromOrder, feedback, t, fetchOrders, fetchSupplierOrdersForLinking, navigate]);

  const handleSubmit = async (payload: Record<string, unknown>) => {
    setLoading(true);

    try {
      let overflowCorrected = false;
      const response = await submitWithOperationalException(
        (confirmedPayload) => createTransportRoute(confirmedPayload),
        payload,
        modal,
        t,
        { correction: cargoOverflowCorrectionOption(t, () => { overflowCorrected = true; }) },
      );
      if (!response) return;
      // The route number is server-assigned, so the confirmation can only name
      // the transport from the response.
      const created = t('transport:routeCreatedSuccessfully', { number: getTransportLabel(response.data) });
      if (overflowCorrected) feedback.success(`${created} ${t('transport:overflowCorrection.applied')}`, { sticky: true });
      else feedback.success(created);
      showTransportSaveWarnings(response.data, feedback, t, canEditTransportCosts);
      const valuationIssues = canEditTransportCosts ? readIncompleteValuationIssues(response.data) : null;
      if (valuationIssues) feedback.warning(incompleteValuationWarning(valuationIssues, t), { sticky: true });
      // Round 2 (U10): straight to the new truck (its costs per m²); its back
      // button still returns to where the planning started.
      navigate(withTransportReturnContext(`/app/transports/${response.data.id}`, returnContext), { replace: true });
    } catch (error: any) {
      // Code -> Polish copy is decided once in
      // transportRouteViews.mapTransportMutationErrorToMessage, wrapped by
      // getTransportErrorMessage so every transport screen agrees.
      feedback.error(getTransportErrorMessage(error, t, 'transport:errorCreatingRoute'), {
        sticky: isBlockingTransportError(error),
      });
    } finally {
      setLoading(false);
    }
  };

  if (!canManageTransports || !startedFromOrder) {
    return null;
  }

  return (
    <div className="transport-route-form-page">
      <TransportRouteFormPageHeader
        title={scopedOrder
          ? t('transport:scopedOrder.pageTitle', {
            number: scopedOrder.displayOrderNumber || scopedOrder.orderNumber,
          })
          : t('transport:createNewRoute')}
        onBack={() => goBack(resolveTransportReturnPath(returnContext, '/app/transports'))}
      />

      <div className="transport-route-form-shell">
        <SharedTransportForm
          availableOrders={availableOrders}
          availableSupplierOrders={availableSupplierOrders}
          supplierOrdersLoading={supplierOrdersLoading}
          orderScopeId={presetOrderId}
          supplierOrdersLoadError={supplierOrdersLoadError}
          onRetrySupplierOrders={fetchSupplierOrdersForLinking}
          initialOrderIds={initialOrderIds}
          initialKind={presetKind}
          initialSupplierOrderIds={initialSupplierOrderIds}
          warehouseAddress={warehouseAddress}
          canEditTransportCosts={canEditTransportCosts}
          canViewGoodsValue={canViewGoodsValue}
          canViewTransportCosts={canViewTransportCosts}
          loading={loading}
          onCancel={() => navigate(resolveTransportReturnPath(returnContext, '/app/transports'))}
          onSubmit={handleSubmit}
        />
      </div>
    </div>
  );
};

export default CreateTransportRoute;
